/**
 * Começar de um modelo: ata, relatório, carta, ofício, declaração, recibo…
 *
 * É o atalho que faz a diferença para quem nunca escreveu uma ata: em vez de uma
 * folha em branco, a pessoa escolhe o modelo, preenche três ou quatro campos e o
 * texto já nasce com a estrutura pronta — para completar no editor.
 *
 * O catálogo e a montagem vivem no **core** (`modelos-de-texto.ts`) e o servidor é
 * quem monta o texto: a tela só manda o id do modelo e o que foi digitado. Assim o
 * mesmo modelo produz o mesmo documento em qualquer aparelho, e acrescentar um
 * modelo novo não mexe na tela.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Loader2, Plus, TriangleAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Campo } from '@/components/ui/label';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import {
  CATEGORIAS_DE_MODELO_DE_TEXTO,
  MODELOS_DE_TEXTO,
  ROTULO_CATEGORIA_DE_MODELO,
  conferirDocumento,
  validarValoresDoModelo,
  type CampoDoModelo,
  type CategoriaDeModeloDeTexto,
  type ModeloDeTexto,
} from '@miolos/core';
import { api, ErroApi } from '@/lib/api';
import { cn } from '@/lib/utils';

export function ModelosDeTexto({ aoCriar }: { aoCriar?: () => void | Promise<void> }) {
  const navegar = useNavigate();
  const [categoria, setCategoria] = useState<CategoriaDeModeloDeTexto | 'TODOS'>('TODOS');
  const [escolhido, setEscolhido] = useState<ModeloDeTexto | null>(null);
  const [valores, setValores] = useState<Record<string, string>>({});
  const [criando, setCriando] = useState(false);
  const [erro, setErro] = useState('');
  /** Só avisa o que falta depois da primeira tentativa — antes disso seria bronca. */
  const [tentou, setTentou] = useState(false);

  const modelos = MODELOS_DE_TEXTO.filter(
    (modelo) => categoria === 'TODOS' || modelo.categoria === categoria,
  );

  const abrir = (modelo: ModeloDeTexto) => {
    const iniciais: Record<string, string> = {};
    for (const campo of modelo.campos) {
      iniciais[campo.id] = campo.tipo === 'ESCOLHA' ? (campo.padrao ?? '') : '';
    }
    setValores(iniciais);
    setErro('');
    setTentou(false);
    setEscolhido(modelo);
  };

  /**
   * Documento: guarda o que foi digitado e, quando o número já se revela, acerta a
   * escolha "pessoa física/jurídica" sozinho. Quem digita 14 posições é pessoa
   * jurídica — não faz sentido obrigar a marcar à mão o que o número já diz.
   */
  const preencherDocumento = (campo: CampoDoModelo, digitado: string) => {
    setValores((atual) => {
      const proximo = { ...atual, [campo.id]: digitado };
      const conferido = conferirDocumento(digitado);
      if (conferido && campo.pessoaDe) {
        proximo[campo.pessoaDe] = conferido.tipo === 'CPF' ? 'FISICA' : 'JURIDICA';
      }
      return proximo;
    });
  };

  /** Ao sair do campo, o número já aparece pontuado (`529.982.247-25`). */
  const formatarDocumento = (campo: CampoDoModelo) => {
    setValores((atual) => {
      const conferido = conferirDocumento(atual[campo.id] ?? '');
      return conferido ? { ...atual, [campo.id]: conferido.formatado } : atual;
    });
  };

  const problemas = escolhido ? validarValoresDoModelo(escolhido.id, valores) : [];
  const problemaDe = (campo: CampoDoModelo) => {
    const preenchido = (valores[campo.id] ?? '').trim().length > 0;
    if (!tentou && !preenchido) return null;
    return problemas.find((problema) => problema.campo === campo.id) ?? null;
  };

  const criar = async () => {
    if (!escolhido) return;
    setTentou(true);
    setErro('');

    const pendencias = validarValoresDoModelo(escolhido.id, valores);
    if (pendencias.length > 0) {
      setErro(pendencias.map((problema) => `${problema.rotulo}: ${problema.mensagem}`).join(' '));
      return;
    }

    setCriando(true);
    try {
      const limpos: Record<string, string> = {};
      for (const [chave, valor] of Object.entries(valores)) {
        if (valor.trim()) limpos[chave] = valor.trim();
      }
      const { texto } = await api.criarTexto({ modelo: { id: escolhido.id, valores: limpos } });
      await aoCriar?.();
      setEscolhido(null);
      navegar(`/textos/${texto.id}`);
    } catch (causa) {
      setErro(causa instanceof ErroApi ? causa.message : 'Não foi possível criar o texto.');
    } finally {
      setCriando(false);
    }
  };

  return (
    <section className="space-y-4">
      <div className="space-y-1">
        <h2 className="text-lg font-semibold tracking-tight">Começar de um modelo</h2>
        <p className="text-sm text-muted-foreground">
          Escolha o tipo de documento, preencha os campos e o texto já nasce pronto para completar.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setCategoria('TODOS')}
          className={cn(
            'rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
            categoria === 'TODOS'
              ? 'border-primary bg-primary/10 text-primary'
              : 'border-border text-muted-foreground hover:border-primary/40',
          )}
        >
          Todos
        </button>
        {CATEGORIAS_DE_MODELO_DE_TEXTO.map((item) => (
          <button
            key={item}
            type="button"
            onClick={() => setCategoria(item)}
            className={cn(
              'rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
              categoria === item
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-border text-muted-foreground hover:border-primary/40',
            )}
          >
            {ROTULO_CATEGORIA_DE_MODELO[item]}
          </button>
        ))}
      </div>

      <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {modelos.map((modelo) => (
          <li key={modelo.id}>
            <button
              type="button"
              onClick={() => abrir(modelo)}
              className="flex h-full w-full flex-col gap-1 rounded-lg border border-border bg-card p-3 text-left transition-colors hover:border-primary/50 hover:bg-primary/5"
            >
              <span className="flex items-center gap-2 text-sm font-medium">
                <FileText className="size-4 text-primary" />
                {modelo.nome}
              </span>
              <span className="text-xs leading-relaxed text-muted-foreground">{modelo.descricao}</span>
              <span className="mt-auto pt-1 text-[11px] text-muted-foreground">
                {modelo.campos.length} campo(s) para preencher
              </span>
            </button>
          </li>
        ))}
      </ul>

      <Dialog open={escolhido !== null} onOpenChange={(aberto) => (aberto ? null : setEscolhido(null))}>
        <DialogContent
          titulo={escolhido?.nome ?? 'Modelo'}
          descricao={escolhido?.descricao ?? ''}
        >
          {escolhido ? (
            <div className="space-y-3">
              {escolhido.campos.map((campo) => {
                const problema = problemaDe(campo);
                return (
                  <Campo key={campo.id} rotulo={campo.rotulo} dica={campo.dica}>
                    {campo.tipo === 'AREA' ? (
                      <textarea
                        value={valores[campo.id] ?? ''}
                        onChange={(evento) =>
                          setValores((atual) => ({ ...atual, [campo.id]: evento.target.value }))
                        }
                        rows={3}
                        className="w-full rounded-md border border-input bg-background p-2 text-sm"
                      />
                    ) : campo.tipo === 'ESCOLHA' ? (
                      <select
                        value={valores[campo.id] ?? ''}
                        onChange={(evento) =>
                          setValores((atual) => ({ ...atual, [campo.id]: evento.target.value }))
                        }
                        className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                      >
                        {campo.opcional ? <option value="">(não informar)</option> : null}
                        {(campo.opcoes ?? []).map((opcao) => (
                          <option key={opcao.valor} value={opcao.valor}>
                            {opcao.rotulo}
                          </option>
                        ))}
                      </select>
                    ) : campo.tipo === 'DOCUMENTO' ? (
                      <input
                        type="text"
                        inputMode="numeric"
                        placeholder="CPF (11 dígitos) ou CNPJ (14)"
                        value={valores[campo.id] ?? ''}
                        onChange={(evento) => preencherDocumento(campo, evento.target.value)}
                        onBlur={() => formatarDocumento(campo)}
                        className={cn(
                          'h-9 w-full rounded-md border bg-background px-3 text-sm',
                          problema ? 'border-destructive' : 'border-input',
                        )}
                      />
                    ) : (
                      <input
                        type={campo.tipo === 'DATA' ? 'date' : campo.tipo === 'HORA' ? 'time' : 'text'}
                        value={valores[campo.id] ?? ''}
                        onChange={(evento) =>
                          setValores((atual) => ({ ...atual, [campo.id]: evento.target.value }))
                        }
                        className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                      />
                    )}
                    {problema ? (
                      <p className="text-[11px] leading-relaxed text-destructive">{problema.mensagem}</p>
                    ) : null}
                  </Campo>
                );
              })}

              <p className="rounded-md bg-muted/60 p-2.5 text-[11px] leading-relaxed text-muted-foreground">
                O que ficar vazio sai como marcador entre colchetes — é só trocar no editor. O resto do
                documento já vem escrito.
              </p>

              {erro ? (
                <p className="flex items-start gap-2 text-xs text-destructive">
                  <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
                  {erro}
                </p>
              ) : null}

              <div className="flex flex-wrap gap-2">
                <Button onClick={() => void criar()} disabled={criando}>
                  {criando ? <Loader2 className="animate-spin" /> : <Plus />}
                  Criar a partir do modelo
                </Button>
                <Button variant="fantasma" onClick={() => setEscolhido(null)}>
                  Cancelar
                </Button>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </section>
  );
}
